import React, { useState, useEffect } from 'react';
import { getDynamicPlayerHeadshot } from '../utils/playerData';

const ParlayBuilder = ({ parlayPicks, onRemovePick, onUpdatePick, onClearParlay }) => {
  const [betAmount, setBetAmount] = useState('');
  const [playerHeadshots, setPlayerHeadshots] = useState({});

  // Load player headshots
  useEffect(() => {
    const loadHeadshots = async () => {
      const headshots = {};
      for (const parlayPick of parlayPicks) {
        const pickLeague = parlayPick.pick.league || parlayPick.game?.league || 'nba';
        const cacheKey = `${pickLeague}:${parlayPick.pick.player}`;
        if (!(cacheKey in headshots)) {
          const headshot = await getDynamicPlayerHeadshot(
            parlayPick.pick.player,
            parlayPick.pick.player_id,
            pickLeague,
            parlayPick.pick.espn_id
          );
          headshots[cacheKey] = headshot;
        }
      }
      setPlayerHeadshots(headshots);
    };
    loadHeadshots();
  }, [parlayPicks]);

  // Convert American odds to decimal odds
  const americanToDecimal = (americanOdds) => {
    if (!americanOdds) return 1;
    if (americanOdds > 0) {
      return (americanOdds / 100) + 1;
    } else {
      return (100 / Math.abs(americanOdds)) + 1;
    }
  };

  // Convert decimal odds to American odds (more precise)
  const decimalToAmerican = (decimalOdds) => {
    if (decimalOdds < 2) {
      // For negative odds: (decimal - 1) * -100
      return Math.round(((decimalOdds - 1) * -100) * 100) / 100;
    } else {
      // For positive odds: (decimal - 1) * 100
      return Math.round(((decimalOdds - 1) * 100) * 100) / 100;
    }
  };

  // Calculate parlay odds
  const calculateParlayOdds = () => {
    if (parlayPicks.length === 0) return null;
    
    // Get all picks with selections
    const selectedPicks = parlayPicks.filter(p => p.selection);
    if (selectedPicks.length === 0) return null;

    // If only one pick, return its odds directly
    if (selectedPicks.length === 1) {
      const { pick, selection } = selectedPicks[0];
      return selection === 'over' ? pick.over_odds : pick.under_odds;
    }

    // Multiply all decimal odds together for multiple picks
    let decimalOdds = 1;
    selectedPicks.forEach(({ pick, selection }) => {
      const odds = selection === 'over' ? pick.over_odds : pick.under_odds;
      decimalOdds *= americanToDecimal(odds);
    });

    return decimalToAmerican(decimalOdds);
  };

  // Calculate payout (total return including bet)
  const calculatePayout = () => {
    const odds = calculateParlayOdds();
    if (!odds || !betAmount || betAmount <= 0) return null;

    const amount = parseFloat(betAmount);
    if (isNaN(amount)) return null;

    let profit = 0;
    if (odds > 0) {
      // Positive odds: profit = bet * (odds / 100)
      profit = amount * (odds / 100);
    } else {
      // Negative odds: profit = bet * (100 / abs(odds))
      profit = amount * (100 / Math.abs(odds));
    }
    
    // Total payout = original bet + profit
    return amount + profit;
  };

  // Calculate probability for a single pick based on hit rates
  const calculatePickProbability = (pick, selection) => {
    if (!selection) return null;

    // Get the relevant hit rate based on selection (over/under)
    // For OVER, use hit rates directly; for UNDER, use 100 - hit rate
    const getHitRate = (rate) => {
      if (rate === null || rate === undefined) return null;
      const num = parseFloat(rate);
      if (isNaN(num)) return null;
      return selection === 'over' ? num : 100 - num;
    };

    // Weighted average: L5 (30%), L10 (25%), L20 (20%), H2H (15%), Season (10%)
    const weights = {
      l5: 0.30,
      l10: 0.25,
      l20: 0.20,
      h2h: 0.15,
      season: 0.10
    };

    const l5 = getHitRate(pick.hit_rate_last_5);
    const l10 = getHitRate(pick.hit_rate_last_10);
    const l20 = getHitRate(pick.hit_rate_last_20);
    const h2h = getHitRate(pick.hit_rate_vs_team);
    const season = getHitRate(pick.hit_rate_last_season);

    let totalWeight = 0;
    let weightedSum = 0;

    if (l5 !== null) {
      weightedSum += l5 * weights.l5;
      totalWeight += weights.l5;
    }
    if (l10 !== null) {
      weightedSum += l10 * weights.l10;
      totalWeight += weights.l10;
    }
    if (l20 !== null) {
      weightedSum += l20 * weights.l20;
      totalWeight += weights.l20;
    }
    if (h2h !== null) {
      weightedSum += h2h * weights.h2h;
      totalWeight += weights.h2h;
    }
    if (season !== null) {
      weightedSum += season * weights.season;
      totalWeight += weights.season;
    }

    if (totalWeight === 0) return null;
    return weightedSum / totalWeight;
  };

  // Calculate overall parlay probability
  const calculateParlayProbability = () => {
    const selectedPicks = parlayPicks.filter(p => p.selection);
    if (selectedPicks.length === 0) return null;

    const probabilities = selectedPicks.map(({ pick, selection }) => 
      calculatePickProbability(pick, selection)
    ).filter(p => p !== null);

    if (probabilities.length === 0) return null;

    // Multiply all probabilities together (convert to decimal first)
    const parlayProbability = probabilities.reduce((acc, prob) => {
      return acc * (prob / 100);
    }, 1);

    return parlayProbability * 100;
  };

  const formatOdds = (odds) => {
    if (!odds) return 'N/A';
    if (odds > 0) {
      return `+${odds}`;
    }
    return odds.toString();
  };

  const getPlayerInitials = (playerName) => {
    const names = playerName.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return playerName.substring(0, 2).toUpperCase();
  };

  const parlayOdds = calculateParlayOdds();
  const payout = calculatePayout();
  const parlayProbability = calculateParlayProbability();

  return (
    <div className="parlay-builder">
      <div className="parlay-builder-header">
        <div className="parlay-builder-title-container">
          <img src="/logos/TURKEYCOIN (16).png" alt="EZ Picks" className="parlay-builder-logo" />
          <h2 className="parlay-builder-title">Parlay Builder</h2>
        </div>
        <button className="parlay-clear-btn" onClick={onClearParlay}>
          Clear All
        </button>
      </div>

      <div className="parlay-picks-list">
        {parlayPicks.map((parlayPick, index) => {
          const { pick, game, selection } = parlayPick;
          const pickLeagueKey = parlayPick.pick.league || parlayPick.game?.league || 'nba';
          const headshotKey = `${pickLeagueKey}:${pick.player}`;
          return (
            <div key={index} className="parlay-pick-item">
              <button 
                className="parlay-remove-btn"
                onClick={() => onRemovePick(index)}
                title="Remove from parlay"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
              
              <div className="parlay-pick-avatar">
                {playerHeadshots[headshotKey] ? (
                  <img 
                    src={playerHeadshots[headshotKey]} 
                    alt={pick.player}
                    className="parlay-pick-headshot"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="parlay-pick-initials" style={{ display: playerHeadshots[pick.player] ? 'none' : 'flex' }}>
                  {getPlayerInitials(pick.player)}
                </div>
              </div>

              <div className="parlay-pick-info">
                <div className="parlay-pick-player">{pick.player}</div>
                <div className="parlay-pick-prop">{pick.prop}</div>
                <div className="parlay-pick-game">{game.game}</div>
              </div>

              <div className="parlay-selection">
                <button
                  className={`parlay-selection-btn ${selection === 'over' ? 'active' : ''}`}
                  onClick={() => onUpdatePick(index, { selection: 'over' })}
                >
                  Over {formatOdds(pick.over_odds)}
                </button>
                <button
                  className={`parlay-selection-btn ${selection === 'under' ? 'active' : ''}`}
                  onClick={() => onUpdatePick(index, { selection: 'under' })}
                >
                  Under {formatOdds(pick.under_odds)}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="parlay-calculator">
        <div className="parlay-odds-display">
          <div className="parlay-odds-label">Parlay Odds:</div>
          <div className="parlay-odds-value">
            {parlayOdds ? formatOdds(parlayOdds) : 'Select picks'}
          </div>
        </div>

        <div className="parlay-probability-display">
          <div className="parlay-probability-label">Probability:</div>
          <div className={`parlay-probability-value ${
            parlayProbability !== null 
              ? parlayProbability > 66 
                ? 'high' 
                : parlayProbability >= 50 
                  ? 'medium' 
                  : 'low'
              : ''
          }`}>
            {parlayProbability !== null ? `${parlayProbability.toFixed(1)}%` : 'N/A'}
          </div>
        </div>

        <div className="parlay-bet-input-container">
          <label htmlFor="bet-amount" className="parlay-bet-label">Bet Amount ($)</label>
          <input
            id="bet-amount"
            type="number"
            className="parlay-bet-input"
            value={betAmount}
            onChange={(e) => setBetAmount(e.target.value)}
            placeholder="0.00"
            min="0"
            step="0.01"
          />
        </div>

        {payout && (
          <div className="parlay-payout-display">
            <div className="parlay-payout-label">Payout:</div>
            <div className="parlay-payout-value">${payout.toFixed(2)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParlayBuilder;

